import { CategoryCascadingSelect } from "@/components/category-cascading-select";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIntakeMessages } from "./intake-messages";
import { TicketIntakeAdditionalFields } from "./ticket-intake-additional-fields";
import { TicketIntakeAttachments } from "./ticket-intake-attachments";
import type { useTicketIntake } from "./use-ticket-intake";

type IntakeForm = ReturnType<typeof useTicketIntake>;

function TicketIntakeCategoryField({ form }: { form: IntakeForm }) {
  const m = useIntakeMessages();
  const { values, busy, errors } = form;

  return (
    <>
      <CategoryCascadingSelect
        id="intake-category"
        categories={form.categories.data ?? []}
        value={values?.categoryId ?? null}
        onValueChange={(id) => void form.setCategory(id)}
        disabled={busy || !form.session || form.categories.isError}
        loading={form.initializing || (Boolean(form.session) && form.categories.isPending)}
        invalid={Boolean(errors.categoryId)}
        descriptionId={errors.categoryId ? "intake-category-error" : undefined}
        label={m.category}
        subcategoryLabel={m.subcategory}
        placeholder={m.categoryPlaceholder}
        commonGroupLabel={m.commonCategories}
        projectGroupLabel={m.projectCategories}
      />
      {errors.categoryId ? <FieldError id="intake-category-error">{errors.categoryId}</FieldError> : null}
      {form.categories.isError ? (
        <FieldError>
          {m.error}
          <Button disabled={busy} type="button" variant="link" onClick={() => void form.categories.refetch()}>
            {m.retry}
          </Button>
        </FieldError>
      ) : null}
    </>
  );
}

export function TicketIntakeTicketFields({ form, layout }: { form: IntakeForm; layout: "default" | "workbench" }) {
  const m = useIntakeMessages();
  const { values, busy, errors } = form;
  const workbench = layout === "workbench";

  return (
    <FieldGroup>
      {!workbench ? <TicketIntakeCategoryField form={form} /> : null}
      <Field data-invalid={Boolean(errors.subject)}>
        <FieldLabel htmlFor="intake-subject" data-required="true">
          {m.subject}
        </FieldLabel>
        <Input
          id="intake-subject"
          disabled={busy}
          maxLength={200}
          required
          placeholder={m.subjectPlaceholder}
          aria-invalid={Boolean(errors.subject)}
          aria-describedby={errors.subject ? "intake-subject-error" : undefined}
          value={values?.subject ?? ""}
          onChange={(event) => form.setBasic("subject", event.target.value)}
        />
        {errors.subject ? <FieldError id="intake-subject-error">{errors.subject}</FieldError> : null}
      </Field>
      <Field>
        <FieldLabel htmlFor="intake-body">{m.body}</FieldLabel>
        <Textarea
          id="intake-body"
          disabled={busy}
          rows={6}
          placeholder={m.bodyPlaceholder}
          value={values?.body ?? ""}
          onChange={(event) => form.setBasic("body", event.target.value)}
        />
      </Field>
      <TicketIntakeAttachments form={form} />
      {workbench ? (
        <FieldGroup className="grid sm:grid-cols-2">
          <FieldGroup className="min-w-0">
            <TicketIntakeCategoryField form={form} />
          </FieldGroup>
        </FieldGroup>
      ) : null}
      {workbench ? <TicketIntakeAdditionalFields form={form} /> : null}
    </FieldGroup>
  );
}
